import type { SWRConfiguration } from 'swr';
import { revalidateCachedErrorOnMount } from 'hooks/useRevalidateCachedErrorOnMount';
import { isCanceledRequestError } from 'utils/isCanceledRequestError';

// Global SWR defaults, applied once at the single render path in index.tsx so
// every app tree inherits them. Focus/reconnect revalidation is disabled so a
// stray refetch can't land mid-edit and overwrite an optimistic
// `mutate(..., { revalidate: false })`. Hooks inherit these rather than
// restating them, so anything rendered outside that root (a detached React
// root, a test with no provider) falls back to SWR's own defaults.
export const swrGlobalConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  // With focus/reconnect revalidation off, a hook whose shouldRetryOnError
  // (false, or a predicate) rejects the cached error never refetches when it
  // mounts while another mounted reader of the same key holds that error.
  // This middleware forces that one request; errors SWR retries are left to
  // its own retry timer.
  use: [revalidateCachedErrorOnMount],
  onError: (error: unknown, key: string) => {
    // Aborts from navigation / tab switches are not application failures.
    // Logging them as console.error makes Datadog RUM treat them as handled errors.
    if (isCanceledRequestError(error)) {
      return;
    }
    // Axios errors carry request headers, including bearer and meeting tokens.
    // Log only the key and message; never serialize the request configuration.
    console.error('SWR error', {
      key,
      message: error instanceof Error ? error.message : undefined,
    });
  },
};

// Org-level reference data: the organization, its brands, users, phone numbers,
// ATS integrations and editor templates. It changes rarely and several
// components on one screen read it, so a 60s window collapses that fan-out into
// a single request. Kept out of swrGlobalConfig because a global window would
// also cover candidate paging, notifications and transcript reads, where a
// refetch after a mutation has to go out right away.
export const referenceDataSwrOptions: SWRConfiguration = {
  dedupingInterval: 60000,
};
